import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { trackEvent } from "@/lib/track-event";
import Reveal from "@/components/motion/Reveal";

interface FaqSectionProps {
  items: Array<{ question: string; answer: string }>;
  idPrefix?: string;
}

const FaqSection = ({ items, idPrefix = "faq" }: FaqSectionProps) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const prefix = isEn ? "/en" : "";

  // REGEL (i18n-Array-Schutz): Aufrufer reichen teils t(..., returnObjects) durch.
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <section className="py-16 lg:py-24 section-tint">
      <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
        <Reveal as="h2" className="text-3xl sm:text-4xl editorial-heading text-foreground mb-8">
          {isEn ? "Frequently Asked Questions" : "Häufige Fragen"}
        </Reveal>
        <Reveal delay={0.05}>
        <Accordion type="single" collapsible className="space-y-2" onValueChange={(v) => { if (v) { const idx = parseInt(v.replace(`${idPrefix}-`, "")); trackEvent("faq_toggle", { question: items[idx]?.question?.slice(0, 80) }); } }}>
          {items.map((item, i) => (
            <AccordionItem key={i} value={`${idPrefix}-${i}`} className="border-b border-border px-1">
              <AccordionTrigger className="text-left font-medium">{item.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        </Reveal>
        <Reveal delay={0.1} className="text-center mt-8 space-y-3">
          <Link to={`${prefix}/faq`}>
            <Button variant="outline" className="font-semibold">
              {isEn ? "View All Questions" : "Alle Fragen ansehen"}
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground">
            {isEn ? "Unfamiliar with a term? See our " : "Begriff unklar? Im "}
            <Link to={`${prefix}/glossar`} className="text-cta hover:underline font-medium">
              {isEn ? "photo booth glossary" : "Fotobox-Glossar"}
            </Link>
            {isEn ? " for clear definitions." : " findest du verständliche Definitionen."}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default FaqSection;
